import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Sheet from '@amzn/meridian/sheet';
import {
  applyFilters,
  setIsAdvancedFilterSheetOpen,
} from 'src/store/adminConfigPageSlice/siteConfigPageSlice/siteConfigPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import React, { useEffect, useState } from 'react';
import FilterDropdown from 'src/components/shared/filterDropdown/filterDropdown';
import { AvailableFilterOptions } from 'src/store/adminConfigPageSlice/siteConfigPageSlice/types';
import { FilterOptions } from 'src/store/adminConfigPageSlice/siteConfigPageSlice/types';
import { extractAvailableFields } from 'src/utils/tableUtils';
import { ActiveFilter } from 'src/types';
import { isEqual } from 'lodash';
import { INITIAL_FILTER_OPTIONS } from 'src/store/adminConfigPageSlice/siteConfigPageSlice/constants';

const AdvancedFilterSheet = () => {
  const { isAdvancedFilterSheetOpen, appliedFilterOptions, tableData } = useAppSelector(
    (state) => state.siteConfigPage,
  );
  const [selectedFilterOptions, setSelectedFilterOptions] = useState<FilterOptions>(appliedFilterOptions);
  const [availableFilterOptions, setAvailableFilterOptions] = useState<AvailableFilterOptions>({
    availableSites: [],
    availableCountries: [],
    availableOrgs: [],
    availableSubOrgs: [],
    availableRegions: [],
    availableBuildingTypes: [],
  });
  const availableActiveOptions: ActiveFilter[] = ['Yes', 'No'];
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!tableData?.length) {
      return;
    }
    setAvailableFilterOptions({
      availableSites: extractAvailableFields(tableData, 'site'),
      availableCountries: extractAvailableFields(tableData, 'country'),
      availableOrgs: extractAvailableFields(tableData, 'org'),
      availableSubOrgs: extractAvailableFields(tableData, 'subOrg'),
      availableRegions: extractAvailableFields(tableData, 'region'),
      availableBuildingTypes: extractAvailableFields(tableData, 'buildingType'),
    });
  }, [tableData]);

  useEffect(() => {
    // if the applied filter options are different from selected filter options, change the selected filter options to the applied filter options (used when removing filters via tags)
    if (!isEqual(appliedFilterOptions, selectedFilterOptions)) {
      setSelectedFilterOptions(appliedFilterOptions);
    }
  }, [appliedFilterOptions]);

  const apply = () => {
    dispatch(applyFilters(selectedFilterOptions));
    dispatch(setIsAdvancedFilterSheetOpen(false));
  };

  const close = () => {
    dispatch(setIsAdvancedFilterSheetOpen(false));
  };

  const reset = () => setSelectedFilterOptions(INITIAL_FILTER_OPTIONS);

  return (
    <Sheet type="overlay" side="end" open={isAdvancedFilterSheetOpen} onClose={close}>
      <Column spacing="500" height={250} width={400}>
        <Row widths={['grid-10', 'grid-2']}>
          <Heading level={4}>Filter Site Configurations</Heading>
          <Button onClick={close} type="icon">
            <Icon tokens={closeTokens}>Close sheet</Icon>
          </Button>
        </Row>
        <Column spacing="400">
          <FilterDropdown
            label="Site Name"
            options={availableFilterOptions.availableSites}
            value={selectedFilterOptions.sites}
            onChange={(sites) => {
              setSelectedFilterOptions({ ...selectedFilterOptions, sites });
            }}
          />
          <FilterDropdown
            label="Country"
            options={availableFilterOptions.availableCountries}
            value={selectedFilterOptions.countries}
            onChange={(countries) => {
              setSelectedFilterOptions({
                ...selectedFilterOptions,
                countries,
              });
            }}
          />
          <FilterDropdown
            label="Org"
            options={availableFilterOptions.availableOrgs}
            value={selectedFilterOptions.orgs}
            onChange={(orgs) => {
              setSelectedFilterOptions({ ...selectedFilterOptions, orgs });
            }}
          />
          <FilterDropdown
            label="Sub Org"
            options={availableFilterOptions.availableSubOrgs}
            value={selectedFilterOptions.subOrgs}
            onChange={(subOrgs) => {
              setSelectedFilterOptions({ ...selectedFilterOptions, subOrgs });
            }}
          />
          <FilterDropdown
            label="Building Type"
            options={availableFilterOptions.availableBuildingTypes}
            value={selectedFilterOptions.buildingTypes}
            onChange={(buildingTypes) => {
              setSelectedFilterOptions({
                ...selectedFilterOptions,
                buildingTypes,
              });
            }}
          />
          <FilterDropdown
            label="Region"
            options={availableFilterOptions.availableRegions}
            value={selectedFilterOptions.regions}
            onChange={(regions) => {
              setSelectedFilterOptions({ ...selectedFilterOptions, regions });
            }}
          />
          <FilterDropdown
            label="Is Active"
            options={availableActiveOptions}
            value={selectedFilterOptions.isActive}
            onChange={(isActive) => {
              setSelectedFilterOptions({
                ...selectedFilterOptions,
                isActive,
              });
            }}
          />
          <Row alignmentHorizontal="center">
            <Button type="secondary" onClick={() => reset()}>
              Reset
            </Button>
            <Button onClick={apply}>Apply</Button>
          </Row>
        </Column>
      </Column>
    </Sheet>
  );
};

export default AdvancedFilterSheet;
